import { QueryOrdering } from '@/api/generated/model';
import { TotalIntentAggregates } from '@/domains/home/components/home-tabs/user-intelligence/_components';
import { ChartMetric } from '@/types/userIntell/chartView';
import { formatDuration, formatNumber, formatPercentage, formatScore } from '@/utils/format.util';
import { SymbolIcon } from '@radix-ui/react-icons';
import { Activity, ArrowDownCircle, ArrowUpCircle, Clock, Heart, MessagesSquare, Users } from 'lucide-react';
import { ReactNode } from 'react';
import { cn } from '@/utils/shad';
import { round } from 'lodash';
import { InteractionAggregatesPaginated } from '@/models/interaction-aggregates/internal-models';
import { IntelligenceTableRow } from '@/models/intelligence-table-row';

const DEFAULT_ICON_SIZE = 12;
const BASE_CLASSNAME = '-mr-1.5';

export function getChartMetricIcon(metric: ChartMetric): ReactNode {
  switch (metric) {
    case 'users':
      return <Users size={DEFAULT_ICON_SIZE} className={cn(BASE_CLASSNAME)} />;
    case 'interactions':
      return <Activity size={DEFAULT_ICON_SIZE} className={cn(BASE_CLASSNAME)} />;
    case 'retention':
      return <SymbolIcon className={cn(BASE_CLASSNAME)} />;
    case 'conversations':
      return <MessagesSquare size={DEFAULT_ICON_SIZE} className={cn(BASE_CLASSNAME)} />;
    case 'sentiment_score':
      return <Heart size={DEFAULT_ICON_SIZE} className={cn(BASE_CLASSNAME)} />;
    case 'avg-interactions':
      return <Activity size={DEFAULT_ICON_SIZE} className={cn(BASE_CLASSNAME)} />;
    case 'avg-conversation-time':
      return <Clock size={DEFAULT_ICON_SIZE} className={cn(BASE_CLASSNAME)} />;
    case 'negative-feedback':
      return <ArrowDownCircle size={DEFAULT_ICON_SIZE} className={cn(BASE_CLASSNAME, 'text-blue-neutral')} />;
    case 'positive-feedback':
      return <ArrowUpCircle size={DEFAULT_ICON_SIZE} className={cn(BASE_CLASSNAME, 'text-blue-neutral')} />;
    default:
      return <Users size={DEFAULT_ICON_SIZE} className={cn(BASE_CLASSNAME)} />;
  }
}

export function getChartSortByIcon(sortBy: QueryOrdering['by']): ReactNode {
  switch (sortBy) {
    case 'n_users':
      return <Users size={DEFAULT_ICON_SIZE} className={cn(BASE_CLASSNAME)} />;
    case 'n_interactions':
      return <Activity size={DEFAULT_ICON_SIZE} className={cn(BASE_CLASSNAME)} />;
    case 'retention':
      return <SymbolIcon className={cn(BASE_CLASSNAME)} />;
    case 'sentiment_score':
      return <Heart size={DEFAULT_ICON_SIZE} className={cn(BASE_CLASSNAME)} />;
    case 'conversation_sentiment_score':
      return <Heart size={DEFAULT_ICON_SIZE} className={cn(BASE_CLASSNAME)} />;
    case 'n_sessions':
      return <MessagesSquare size={DEFAULT_ICON_SIZE} className={cn(BASE_CLASSNAME)} />;
    case 'session_length':
      return <Activity size={DEFAULT_ICON_SIZE} className={cn(BASE_CLASSNAME)} />;
    case 'session_time':
      return <Clock size={DEFAULT_ICON_SIZE} className={cn(BASE_CLASSNAME)} />;
    case 'negative_feedback':
      return <ArrowDownCircle size={DEFAULT_ICON_SIZE} className={cn(BASE_CLASSNAME, 'text-red-800')} />;
    case 'positive_feedback':
      return <ArrowUpCircle size={DEFAULT_ICON_SIZE} className={cn(BASE_CLASSNAME, 'text-green-800')} />;
    default:
      return <Users size={DEFAULT_ICON_SIZE} className={cn(BASE_CLASSNAME)} />;
  }
}

export function getChartSortByValue(
  sortByCriteria: QueryOrdering['by'],
  row: IntelligenceTableRow,
): string | undefined {
  switch (sortByCriteria) {
    case 'n_users':
      return `${row.n_users}`;
    case 'n_interactions':
      return `${row.n_interactions}`;
    case 'retention':
      return row.retention ? formatPercentage(row.retention, { inputScale: 'decimal', decimals: 0 }) : '0%';
    case 'n_sessions':
      return row.n_sessions ? formatNumber(row.n_sessions) : '0';
    case 'sentiment_score':
      return row.sentiment_score ? formatScore(row.sentiment_score, { decimals: 2 }) : '3';
    case 'conversation_sentiment_score':
      return row.sentiment_score ? formatScore(row.sentiment_score, { decimals: 2 }) : '3';
    case 'session_length':
      return row.session_length ? `${round(row.session_length, 2).toString()}` : '0';
    case 'session_time':
      return row.session_time ? formatDuration(row.session_time, { inputUnit: 'seconds', style: 'human' }) : '0';
    case 'negative_feedback':
      return row.n_negative_feedback ? `${row.n_negative_feedback}` : '0';
    case 'positive_feedback':
      return row.n_positive_feedback ? `${row.n_positive_feedback}` : '0';
    default:
      return undefined;
  }
}

export function computeChartSortByTotalPercentage(
  sortByCriteria: QueryOrdering['by'],
  aggregates: TotalIntentAggregates,
  data: InteractionAggregatesPaginated | undefined,
): string | undefined {
  if (sortByCriteria === 'n_users') {
    return aggregates?.total_users && data?.nUsers
      ? formatPercentage(data?.nUsers / aggregates.total_users, { inputScale: 'decimal', decimals: 0 })
      : undefined;
  }

  if (sortByCriteria === 'n_interactions') {
    return aggregates?.total_interactions && data?.nInteractions
      ? formatPercentage(data?.nInteractions / aggregates.total_interactions, { inputScale: 'decimal', decimals: 0 })
      : undefined;
  }

  return undefined;
}

export function isPercentageSortByValue(sortBycriteria: QueryOrdering['by']): boolean {
  return sortBycriteria === 'n_users' || sortBycriteria === 'n_interactions';
}

export function getChartSortByTotalValue(
  sortByCriteria: QueryOrdering['by'],
  rows: IntelligenceTableRow[] | undefined,
  data: InteractionAggregatesPaginated | undefined,
): string | undefined {
  switch (sortByCriteria) {
    case 'n_users':
      return `${formatNumber(data?.nUsers)}`;
    case 'n_interactions':
      return `${formatNumber(data?.nInteractions)}`;
    case 'retention':
      return rows
        ? formatPercentage(rows.reduce((acc, row) => acc + (row.retention || 0), 0) / rows.length, {
            inputScale: 'decimal',
            decimals: 0,
          })
        : undefined;
    case 'n_sessions':
      return rows ? `${formatNumber(rows.reduce((acc, row) => acc + (row.n_sessions || 0), 0))}` : undefined;
    case 'session_length':
      return rows
        ? `${(rows.reduce((acc, row) => acc + (row.session_length || 0), 0) / rows.length).toFixed(2)}`
        : undefined;
    case 'session_time':
      return rows
        ? formatDuration(rows.reduce((acc, row) => acc + (row.session_time || 0), 0) / rows.length, {
            inputUnit: 'seconds',
            style: 'human',
          })
        : undefined;
    case 'negative_feedback':
      return rows ? `${formatNumber(rows.reduce((acc, row) => acc + (row.n_negative_feedback || 0), 0))}` : undefined;
    case 'positive_feedback':
      return rows ? `${formatNumber(rows.reduce((acc, row) => acc + (row.n_positive_feedback || 0), 0))}` : undefined;
    default:
      return undefined;
  }
}
